import { useCallback, useMemo } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { format, addHours } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { useProfile } from '@shared/api/useProfile';
import { useAddAppointmentByTeacherModal } from '@features/teacher/add-appointment-by-teacher/model/use-add-appointment-by-teacher-modal';
import { toTzDate } from '../../lib/date';
import s from './teacher-edit-mode.module.scss';
import { ClassCard } from '@features/schedule/ui/class-сard/class-сard';

export const TeacherEditMode = ({
  selectedDate,
  appointments,
  timeSlots,
  selectedWeekdayKey,
  rangeParams,
  onSave,
  onCancel,
}) => {
  const { data: profile } = useProfile();
  const timezone = profile?.timezone;
  const { open: openAddAppointmentModal } = useAddAppointmentByTeacherModal();

  const dateStr = useMemo(
    () => format(toTzDate(selectedDate, timezone), 'yyyy-MM-dd'),
    [selectedDate, timezone]
  );

  const dayTimeSlots = useMemo(() => {
    if (!timeSlots) return [];

    return timeSlots
      .filter((slot) => slot.weekday === selectedWeekdayKey)
      .map((slot) => slot.time)
      .sort((a, b) => {
        const [aHours, aMinutes] = a.split(':').map(Number);
        const [bHours, bMinutes] = b.split(':').map(Number);
        return aHours * 60 + aMinutes - (bHours * 60 + bMinutes);
      });
  }, [timeSlots, selectedWeekdayKey]);

  const appointmentsByTime = useMemo(() => {
    const map = {};
    appointments.forEach((apt) => {
      const time = format(toTzDate(new Date(apt.starts_at), timezone), 'HH:mm');
      map[time] = apt;
    });
    return map;
  }, [appointments, timezone]);

  const isSlotAddAllowed = useCallback((time) => {
    if (!timezone) return false;
    const now = toTzDate(new Date(), timezone);
    const minTime = addHours(now, 1);
    const [hours, minutes] = time.split(':');
    const slotDateTime = new TZDate(`${dateStr}T${hours}:${minutes}:00`, timezone);
    return slotDateTime.getTime() >= minTime.getTime();
  }, [timezone, dateStr]);

  const handleAddClick = (time) => {
    openAddAppointmentModal({
      date: selectedDate,
      time,
      params: rangeParams,
    });
  };

  const visibleSlots = useMemo(() => {
    const appointmentTimes = Object.keys(appointmentsByTime);
    const futureFreeSlots = dayTimeSlots.filter(
      (time) => !appointmentsByTime[time] && isSlotAddAllowed(time)
    );
    const uniqueTimes = Array.from(new Set([...appointmentTimes, ...futureFreeSlots]));

    return uniqueTimes.sort((a, b) => {
      const [aHours, aMinutes] = a.split(':').map(Number);
      const [bHours, bMinutes] = b.split(':').map(Number);
      return aHours * 60 + aMinutes - (bHours * 60 + bMinutes);
    });
  }, [dayTimeSlots, appointmentsByTime, isSlotAddAllowed]);

  if (visibleSlots.length === 0) {
    return (
      <div className={s.editMode}>
        <div className={s.emptySlots}>
          <span>Нет доступных слотов для записи</span>
        </div>
        <div className={s.actions}>
          <button
            className={s.saveButton}
            onClick={onSave}
            type="button"
          >
            Сохранить
          </button>
          <button
            className={s.cancelButton}
            onClick={onCancel}
            type="button"
          >
            Отменить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.editMode}>
      <div className={s.slotsList}>
        {visibleSlots.map((time) => {
          const appointment = appointmentsByTime[time];

          if (appointment) {
            return (
              <ClassCard
								key={appointment.id || appointment.starts_at}
								{...appointment}
							/>
            );
          }

          return (
            <div key={time} className={s.slotRow}>
              <span className={s.slotTime}>{time}</span>
              <button
                className={s.addButton}
                onClick={() => handleAddClick(time)}
                type="button"
              >
                <span>Добавить занятие</span>
                <IconPlus size={20} />
              </button>
            </div>
          );
        })}
      </div>

      <div className={s.actions}>
        <button
          className={s.saveButton}
          onClick={onSave}
          type="button"
        >
          Сохранить
        </button>
        <button
          className={s.cancelButton}
          onClick={onCancel}
          type="button"
        >
          Отменить
        </button>
      </div>
    </div>
  );
};
